import { logout, validateSession } from './sessions'

type Ok<T> = { ok: true; value: T }
type Err<T> = { ok: false; error: T }

export type Result<O, E> = Ok<O> | Err<E>

interface ErroredFormat {
    ok: false
    status?: number
    error: Error
    requestId?: string
    message?: string
    /**
     * Habitat Admin's error handler replaces the message on every
     * error it recognises, so `name` is the only part of a failure
     * that survives the trip. Pages that need to say something
     * specific about a failure switch on this.
     */
    name?: string
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    data?: any
}

export default class ApiInteractor {
    private static _getAccessToken(): Result<string, Error> {
        try {
            const session = localStorage.getItem('session')

            if (!session) {
                return {
                    ok: false,
                    error: new Error('No session found'),
                }
            }

            // Not sure what causes this, possibly something in the inner
            // workings of `usehooks-ts` that double stringifies the stored object
            const validated = validateSession(JSON.parse(JSON.parse(session)))

            if (!validated) {
                return {
                    ok: false,
                    error: new Error(`JWT not valid.`),
                }
            }

            return { ok: true, value: validated.accessToken }
        } catch (error) {
            console.error(error)
            return { ok: false, error: error as Error }
        }
    }

    /**
     * Helper to build the headers object,
     * including X-Org-Id if localStorage has 'selectedOrgId'.
     */
    private static _buildHeaders(token: string) {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
        }

        return headers
    }

    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    private static _handleSuccess(jsonRes: any) {
        return jsonRes.result ?? jsonRes.data
    }

    private static _handleErrorResponse(jsonResp: {
        status: number
        name: string
    }) {
        if (jsonResp.status === 401) {
            logout()
            return
        }
    }

    static async _getWithJwt<ResponseType = object>(
        url: string
    ): Promise<Ok<ResponseType> | ErroredFormat> {
        const accessTokenResult = ApiInteractor._getAccessToken()
        if (!accessTokenResult.ok) {
            return { ok: false, error: accessTokenResult.error }
        }

        const headers = ApiInteractor._buildHeaders(accessTokenResult.value)

        const res = await fetch(url, {
            method: 'GET',
            headers,
        })

        try {
            const jsonRes = await res.json()

            const requestId = res.headers.get('x-request-id')
            if (!res.ok) {
                ApiInteractor._handleErrorResponse(jsonRes)
                return {
                    // Spread the body first so its `error`/`status` fields can't clobber the
                    // real Error and HTTP status we set below.
                    ...jsonRes,
                    ok: false,
                    error: new Error(
                        `Failed to request data from server: ${res.status} ${res.statusText}`
                    ),
                    status: res.status,
                    requestId,
                }
            }

            return { ok: true, value: ApiInteractor._handleSuccess(jsonRes) }
        } catch (error) {
            return { ok: false, error: error as Error, status: res.status }
        }
    }

    static async _getBlobWithJwt(
        url: string,
        accept?: string
    ): Promise<Ok<Blob> | ErroredFormat> {
        const accessTokenResult = ApiInteractor._getAccessToken()
        if (!accessTokenResult.ok) {
            return { ok: false, error: accessTokenResult.error }
        }

        const headers = ApiInteractor._buildHeaders(accessTokenResult.value)

        if (accept) {
            headers['accept'] = accept
        }
        const res = await fetch(url, {
            method: 'GET',
            headers,
        })

        try {
            const blobRes = await res.blob()

            const requestId = res.headers.get('x-request-id') ?? ''
            if (!res.ok) {
                ApiInteractor._handleErrorResponse({
                    status: res.status,
                    name: '',
                })
                return {
                    ok: false,
                    error: new Error(
                        `Failed to request data from server: ${res.status} ${res.statusText}`
                    ),
                    requestId,
                }
            }

            return { ok: true, value: blobRes }
        } catch (error) {
            console.error(error)
            return { ok: false, error: error as Error }
        }
    }

    static async _postWithJwt<ResponseType = object, BodyType = object>(
        url: string,
        body?: BodyType,
        signal?: AbortSignal
    ): Promise<Ok<ResponseType> | ErroredFormat> {
        const accessTokenResult = ApiInteractor._getAccessToken()
        if (!accessTokenResult.ok) {
            return { ok: false, error: accessTokenResult.error }
        }

        const headers = ApiInteractor._buildHeaders(accessTokenResult.value)

        const res = await fetch(url, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
            signal,
        })

        try {
            const jsonRes = await res.json()
            const requestId = res.headers.get('x-request-id')

            if (!res.ok) {
                console.error(`Error: ${res.status} ${res.statusText}`)
                ApiInteractor._handleErrorResponse(jsonRes)
                return {
                    // Spread the body first so its `error`/`status` fields can't clobber the
                    // real Error and HTTP status we set below.
                    ...jsonRes,
                    ok: false,
                    error: new Error(
                        `Failed to post to server: ${res.status} ${res.statusText}`
                    ),
                    status: res.status,
                    requestId,
                }
            }

            if (jsonRes.error) {
                return { ok: false, error: jsonRes.error, status: res.status }
            }

            return { ok: true, value: ApiInteractor._handleSuccess(jsonRes) }
        } catch (error) {
            console.error(error)
            return { ok: false, error: error as Error, status: res.status }
        }
    }

    static async _patchWithJwt<ResponseType = object, BodyType = object>(
        url: string,
        body: BodyType
    ): Promise<Ok<ResponseType> | ErroredFormat> {
        const accessTokenResult = ApiInteractor._getAccessToken()
        if (!accessTokenResult.ok) {
            return { ok: false, error: accessTokenResult.error }
        }

        const headers = ApiInteractor._buildHeaders(accessTokenResult.value)

        const res = await fetch(url, {
            method: 'PATCH',
            headers,
            body: JSON.stringify(body),
        })

        try {
            const jsonData = await res.json()
            const requestId = res.headers.get('x-request-id')

            if (!res.ok) {
                console.error(`Error: ${res.status} ${res.statusText}`)
                ApiInteractor._handleErrorResponse(jsonData)
                return {
                    // Spread the body first so its `error`/`status` fields can't clobber the
                    // real Error and HTTP status we set below.
                    ...jsonData,
                    ok: false,
                    error: new Error(
                        `Failed to patch to server: ${res.status} ${res.statusText}`
                    ),
                    status: res.status,
                    requestId,
                }
            }

            if (jsonData.error) {
                return { ok: false, error: jsonData.error, status: res.status }
            }
            return { ok: true, value: ApiInteractor._handleSuccess(jsonData) }
        } catch (error) {
            return { ok: false, error: error as Error }
        }
    }

    static async _deleteWithJwt<ResponseType = string, BodyType = unknown>(
        url: string,
        body?: BodyType
    ): Promise<Ok<ResponseType> | ErroredFormat> {
        const accessTokenResult = ApiInteractor._getAccessToken()
        if (!accessTokenResult.ok) {
            return { ok: false, error: accessTokenResult.error }
        }

        const headers = ApiInteractor._buildHeaders(accessTokenResult.value)

        const res = await fetch(url, {
            method: 'DELETE',
            headers,
            body: body ? JSON.stringify(body) : undefined,
        })
        const requestId = res.headers.get('x-request-id') ?? undefined

        try {
            const jsonData = await res.json()

            if (!res.ok) {
                console.error(`Error: ${res.status} ${res.statusText}`)
                ApiInteractor._handleErrorResponse(jsonData)
                return {
                    // Spread the body first so its `error`/`status` fields can't clobber the
                    // real Error and HTTP status we set below. The other verbs already do
                    // this; without it a caller has no way to tell one failure from another.
                    ...jsonData,
                    ok: false,
                    error: new Error(
                        `Failed to delete on server: ${res.status} ${res.statusText}`
                    ),
                    status: res.status,
                    requestId,
                }
            }

            const data = await jsonData
            if (data.error) {
                return { ok: false, error: data.error, requestId }
            }
            return { ok: true, value: ApiInteractor._handleSuccess(jsonData) }
        } catch (error) {
            return { ok: false, error: error as Error }
        }
    }
}
